// SupportWidget (Story #4613, Stream 8a) — mockup 01. A persistent floating '?'
// launcher that opens a slide-over panel on desktop and a full-screen sheet below
// 768px. Two tabs: New request (the form) and My tickets (a compact preview that
// deep-links into the full /support page). Mounted once in AppShell so it is
// available on every authenticated screen.

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { HelpCircle, X } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { listMyTickets } from '../../services/support'
import type { SupportTicket } from '../../types/support'
import { deriveDisplayStatus, formatAedt } from '../../utils/supportDisplay'
import { DisplayStatusChip, RequestTypeBadge } from './SupportBadges'
import NewRequestForm from './NewRequestForm'

type Tab = 'new' | 'list'

export default function SupportWidget() {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<Tab>('new')
  const [tickets, setTickets] = useState<SupportTicket[]>([])

  const loadTickets = useCallback(() => {
    listMyTickets()
      .then((r) => setTickets(r.tickets ?? []))
      .catch(() => setTickets([]))
  }, [])

  useEffect(() => {
    if (open) loadTickets()
  }, [open, loadTickets])

  const openTicket = useCallback(
    (ticketId: string) => {
      setOpen(false)
      navigate(`/support?ticket=${ticketId}`)
    },
    [navigate],
  )

  // The widget is only meaningful for a signed-in user.
  if (!isAuthenticated) return null

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => {
            setTab('new')
            setOpen(true)
          }}
          aria-label="Open support"
          className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-iflow-500 text-white shadow-lg shadow-iflow-900/30 hover:bg-iflow-600"
        >
          <HelpCircle size={24} />
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Support"
          className="fixed inset-0 z-50 flex flex-col bg-white md:inset-auto md:bottom-24 md:right-6 md:max-h-[80vh] md:w-[420px] md:rounded-2xl md:border md:border-iflow-900/10 md:shadow-2xl md:shadow-iflow-900/25"
        >
          {/* Header */}
          <div className="flex items-start justify-between bg-gradient-to-br from-iflow-900 to-iflow-700 px-4 py-3.5 text-white md:rounded-t-2xl">
            <div>
              <p className="font-bold">Support</p>
              <p className="text-[11.5px] text-white/75">We usually respond within 1 business day (AEDT)</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close support" className="rounded p-1 text-white/80 hover:bg-white/10">
              <X size={18} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200">
            <button
              type="button"
              onClick={() => setTab('new')}
              className={`flex-1 py-2.5 text-[13px] ${tab === 'new' ? 'border-b-2 border-iflow-500 font-bold text-iflow-700' : 'text-slate-500'}`}
            >
              New request
            </button>
            <button
              type="button"
              onClick={() => setTab('list')}
              className={`flex-1 py-2.5 text-[13px] ${tab === 'list' ? 'border-b-2 border-iflow-500 font-bold text-iflow-700' : 'text-slate-500'}`}
            >
              My tickets ({tickets.length})
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 pb-5 pt-1">
            {tab === 'new' ? (
              <NewRequestForm
                userId={user?.id}
                myTickets={tickets}
                onSubmitted={(t) => {
                  loadTickets()
                  openTicket(t.ticket_id)
                }}
                onCancel={() => setOpen(false)}
                onOpenTicket={openTicket}
              />
            ) : (
              <div className="pt-3">
                {tickets.length === 0 ? (
                  <p className="px-1 py-6 text-center text-[13px] text-slate-500">
                    You haven’t raised any support tickets yet.
                  </p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {tickets.map((t) => (
                      <li key={t.ticket_id}>
                        <button
                          type="button"
                          onClick={() => openTicket(t.ticket_id)}
                          className="flex w-full flex-col gap-1 px-1 py-2.5 text-left hover:bg-iflow-50"
                        >
                          <span className="flex items-center gap-2">
                            <span className="text-[12px] font-semibold text-iflow-600">{t.ref}</span>
                            <DisplayStatusChip status={deriveDisplayStatus(t, user?.id)} />
                          </span>
                          <span className="truncate text-[13px] text-slate-800">{t.subject}</span>
                          <span className="flex items-center gap-2 text-[11px] text-slate-500">
                            <RequestTypeBadge type={t.request_type} />
                            {formatAedt(t.updated_at)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    navigate('/support')
                  }}
                  className="mt-3 w-full rounded-lg border border-iflow-200 py-2 text-[13px] font-semibold text-iflow-700 hover:bg-iflow-50"
                >
                  Open full support centre
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
